import React, { useEffect, useMemo, useCallback, useState, useRef, Fragment, useReducer } from 'react'
import { createEditor } from 'vendor/slate'
import { Slate, Editable, withReact } from 'vendor/slate-react'
import styled from 'styled-components'
import { ELEMENT_TYPES } from 'Documents/editor/slate/types'
import debounce from 'lodash-es/debounce'
import { Link } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import TimeAgo from 'react-timeago'
import propTypes from 'prop-types'
import { sanitizeHeaderAnchors } from 'Documents/helpers'
import { withLink } from 'Documents/editor/slate/plugins/link'
import { withDefaults } from 'Documents/editor/slate/plugins/defaults'
import { withTabs } from 'Documents/editor/slate/plugins/tabs'
import { withLists } from 'Documents/editor/slate/plugins/lists'
import { withCodeTabs } from 'Documents/editor/slate/plugins/code'
import { withQuotes } from 'Documents/editor/slate/plugins/quote'
import { withTables } from 'Documents/editor/slate/plugins/table'
import { withHints } from 'Documents/editor/slate/plugins/hint'
import { withApi } from 'Documents/editor/slate/plugins/api'
import { EditorDispatch, editorReducer } from 'Documents/editor/slate/reducer'
import Leaf from 'Documents/editor/slate/leaf'
// Call a special publish version of Element to prevent extra dependencies loading
import ElementPublish from 'Documents/editor/slate/element'
import { extractDocumentToc } from 'Documents/editor/slate/plugins/rawMarkdown/headings'

require('intersection-observer')

const OFFSET_TOP = 30
const TOC_MARGIN_BOTTOM = 55

function usePrevious(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const initialValue = [
  {
    type: ELEMENT_TYPES.default,
    parent: { type: null },
    children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }]
  }
]

const Editor = ({
  sectionData,
  themeColor,
  namespace,
  workspace,
  slug,
  version,
  currentSectionID,
  workspaceId,
  fileCdnPath,
  fileDownloadPath
}) => {
  const editorScrollWrapperRef = useRef()
  const tocWrapperRef = useRef()
  const { hash } = window.location
  const previousHash = usePrevious(hash)
  const previousSection = sectionData && sectionData.previous_section ? JSON.parse(sectionData.previous_section) : null
  const nextSection = sectionData && sectionData.next_section ? JSON.parse(sectionData.next_section) : null
  const [state, dispatch] = useReducer(editorReducer)
  const [tocHeight, setTocHeight] = useState(null)

  const calculateTocHeight = () => {
    if (editorScrollWrapperRef && editorScrollWrapperRef.current) {
      const containerNodeBoundingClient = editorScrollWrapperRef.current.getBoundingClientRect()
      if (containerNodeBoundingClient) setTocHeight(containerNodeBoundingClient.height - TOC_MARGIN_BOTTOM)
    }
  }

  const editor = useMemo(
    () =>
      withApi(
        withHints(withTables(withQuotes(withCodeTabs(withLists(withTabs(withLink(withDefaults(withReact(createEditor())))))))))
      ),
    []
  )

  const renderPreviousPagination = previousSection => (
    <>
      <PaginationArrowContainer className="arrow-container">
        <i className="far fa-arrow-left" />
      </PaginationArrowContainer>
      <PaginationTitleContainer $alignRight>
        <PaginationSubhead className="sub-head">Previous</PaginationSubhead>
        <PaginationHead className="head">{previousSection.title}</PaginationHead>
      </PaginationTitleContainer>
    </>
  )

  const renderNextPagination = nextSection => (
    <>
      <PaginationTitleContainer>
        <PaginationSubhead className="sub-head">Next</PaginationSubhead>
        <PaginationHead className="head">{nextSection.title}</PaginationHead>
      </PaginationTitleContainer>
      <PaginationArrowContainer className="arrow-container">
        <i className="far fa-arrow-right" />
      </PaginationArrowContainer>
    </>
  )

  const renderPagination = () => (
    <>
      {previousSection && (
        <PreviousPagination
          to={`/${namespace}/${workspace}/${slug}/${version}/${previousSection.id}`}
          $hasbothpaginators={previousSection && previousSection && nextSection && nextSection}
          $themecolor={themeColor}
        >
          {renderPreviousPagination(previousSection)}
        </PreviousPagination>
      )}
      {nextSection && (
        <NextPagination
          to={`/${namespace}/${workspace}/${slug}/${version}/${nextSection.id}`}
          $hasbothpaginators={previousSection && previousSection && nextSection && nextSection}
          $themecolor={themeColor}
        >
          {renderNextPagination(nextSection)}
        </NextPagination>
      )}
    </>
  )

  const positionToc = debounce(() => {
    if (editorScrollWrapperRef) {
      document.querySelector('#toc-ul').style.top = `${editorScrollWrapperRef.current.scrollTop + OFFSET_TOP}px`
    }
  }, 50)

  const renderLeaf = useCallback(props => <Leaf {...props} previousHash={previousHash} hash={hash} />, [hash, previousHash])
  const renderElement = useCallback(
    props => {
      const extraProps = {}

      return (
        <ElementPublish
          {...props}
          editor={editor}
          workspaceId={workspaceId}
          themeColor={themeColor}
          workspacePath={workspace}
          username={namespace}
          publishedDocument
          section={currentSectionID}
          version={version}
          namespace={namespace}
          workspace={workspace}
          slug={slug}
          fileCdnPath={fileCdnPath}
          fileDownloadPath={fileDownloadPath}
          {...extraProps}
        />
      )
    },
    [editor, workspaceId, themeColor, workspace, namespace, fileCdnPath, fileDownloadPath]
  )

  const toc = extractDocumentToc(sectionData && sectionData.slate_json)

  useEffect(() => {
    calculateTocHeight()
  }, [toc.length])

  useEffect(() => {
    const debouncedHandleResize = debounce(() => {
      calculateTocHeight()
    }, 100)

    window.addEventListener('resize', debouncedHandleResize)

    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
    }
  }, [toc.length])

  const useIntersectionObserver = setActiveTocId => {
    const headingElementsRef = useRef({})
    useEffect(() => {
      const callback = headings => {
        headingElementsRef.current = headings.reduce((map, headingElement) => {
          map[headingElement.target.id] = headingElement
          return map
        }, headingElementsRef.current)

        // Get all headings that are currently visible on the page
        const visibleHeadings = []
        Object.keys(headingElementsRef.current).forEach(key => {
          const headingElement = headingElementsRef.current[key]
          if (headingElement.isIntersecting) visibleHeadings.push(headingElement)
        })

        const getIndexFromId = id => headingElements.findIndex(heading => heading.id === id)

        // If there is only one visible heading, this is our "active" heading
        if (visibleHeadings.length === 1) {
          setActiveTocId(visibleHeadings[0].target.id)
          // If there is more than one visible heading,
          // choose the one that is closest to the top of the page
        } else if (visibleHeadings.length > 1) {
          const sortedVisibleHeadings = visibleHeadings.sort((a, b) => getIndexFromId(a.target.id) > getIndexFromId(b.target.id))

          setActiveTocId(sortedVisibleHeadings[0].target.id)
        }
      }

      const observer = new IntersectionObserver(callback, { root: document.querySelector('#editor-wrapper'), rootMargin: '65px' })

      const headingElements = Array.from(document.querySelectorAll('h1, h2'))
      headingElements.forEach(element => observer.observe(element))

      return () => observer.disconnect()
    }, [setActiveTocId])
  }

  const TableOfContents = () => {
    const [activeTocId, setActiveTocId] = useState()
    useIntersectionObserver(setActiveTocId)

    return (
      <TocUL ref={tocWrapperRef}>
        {toc &&
          toc.map((item, index) => (
            <TocLI
              $themeColor={themeColor}
              key={index}
              className={`toc-item toc-item-${sanitizeHeaderAnchors(item.children[0].text)}`}
              active={sanitizeHeaderAnchors(item.children[0].text) === activeTocId}
            >
              {item.type === ELEMENT_TYPES.header_one && (
                <TocH1
                  to={`/${namespace}/${workspace}/${slug}/${version}/${currentSectionID}#${sanitizeHeaderAnchors(
                    item.children[0].text
                  )}`}
                >
                  {item.children[0].text}
                </TocH1>
              )}
              {item.type === ELEMENT_TYPES.header_two && (
                <TocH2
                  to={`/${namespace}/${workspace}/${slug}/${version}/${currentSectionID}#${sanitizeHeaderAnchors(
                    item.children[0].text
                  )}`}
                >
                  {item.children[0].text}
                </TocH2>
              )}
            </TocLI>
          ))}
      </TocUL>
    )
  }

  return (
    <EditorWrapper
      id="editor-wrapper"
      ref={editorScrollWrapperRef}
      onScroll={() => {
        positionToc()
      }}
    >
      <Slate editor={editor} value={sectionData && sectionData.slate_json ? sectionData.slate_json : initialValue}>
        <EditorScrollWrapper readOnly>
          <SlateWrapper>
            <EditablePaginationWrapper>
              {sectionData && sectionData.title && <Title>{sectionData.title}</Title>}
              <EditableWrapper>
                <EditorDispatch.Provider value={{ state, dispatch }}>
                  <Editable renderElement={renderElement} renderLeaf={renderLeaf} readOnly />
                </EditorDispatch.Provider>
              </EditableWrapper>
              <PaginationWrapper $lastModified={sectionData.last_modified}>{renderPagination()}</PaginationWrapper>
              {sectionData && sectionData.last_modified && (
                <LastModifiedWrapper $themeColor={themeColor}>
                  <hr />
                  <div className="d-flex justify-content-between">
                    <div>
                      Powered by&nbsp;
                      <a href="https://codegiant.io" target="_blank" rel="noreferrer">
                        Codegiant
                      </a>
                    </div>
                    <div>
                      Updated
                      <TimeAgo
                        date={sectionData.last_modified}
                        minPeriod={60}
                        formatter={(value, unit) => ` ${value} ${unit}${value > 1 ? 's' : ''} ago`}
                      />
                    </div>
                  </div>
                </LastModifiedWrapper>
              )}
            </EditablePaginationWrapper>
          </SlateWrapper>
          <RightContentsWrapper>
            <TocULWrapper id="toc-ul" style={{ position: 'absolute', top: `${OFFSET_TOP}px` }} $tocHeight={tocHeight}>
              <TableOfContents />
            </TocULWrapper>
          </RightContentsWrapper>
        </EditorScrollWrapper>
      </Slate>
    </EditorWrapper>
  )
}

Editor.propTypes = {
  namespace: propTypes.string,
  workspace: propTypes.string,
  workspaceId: propTypes.string,
  fileCdnPath: propTypes.string,
  fileDownloadPath: propTypes.string,
  slug: propTypes.string,
  version: propTypes.string,
  // The currently navigated section ID
  currentSectionID: propTypes.string,
  sectionData: propTypes.shape({
    id: propTypes.number,
    group_id: propTypes.number,
    tab_id: propTypes.number,
    title: propTypes.string,
    color: propTypes.string,
    icon: propTypes.string,
    next_section: propTypes.string,
    previous_section: propTypes.string,
    slate_json: propTypes.string,
    last_modified: propTypes.string
  }),
  themeColor: propTypes.string
}

export default Editor

const EditorWrapper = styled.div`
  position: relative;
  min-height: 100%;
  font-size: 1rem;
  overflow: auto;
  .search-match {
    background: yellow !important;
  }
`

const EditableWrapper = styled.div`
  flex-grow: 1;
  color: ${props => props.theme.textGreyTintedDarker};
`

const EditablePaginationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 0;
`

const EditorScrollWrapper = styled.div`
  display: flex;
  flex: 1;
  min-height: 100%;
  position: relative;
`

const RightContentsWrapper = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: 220px;
  @media (max-width: 992px) {
    display: none;
  }
`

const Title = styled.div`
  font-weight: 500;
  color: ${props => props.theme.textDocumentSectionHeader};
  padding: 20px 0;
  font-size: 33px;
`

const SlateWrapper = styled.div`
  flex: 1 1 auto;
  word-break: break-word;
  max-width: 750px;
  min-width: 0;
  position: relative;
  flex-direction: column;
  margin: 0 72px;
`

const TocULWrapper = styled.div`
  position: absolute;
  left: auto;
  min-width: 220px;
  width: 100%;
  max-height: ${props => (props.$tocHeight ? `${props.$tocHeight}px` : 'auto')};
  overflow: hidden;
  &:hover {
    overflow-y: auto;
  }
`

const TocUL = styled.ul`
  display: flex;
  padding: 0;
  margin: 0;
  flex-direction: column;
  list-style-type: none;
`

const TocLI = styled.li`
  padding: 0;
  border-left: 1px solid;
  cursor: pointer;
  border-left-color: ${({ active, $themeColor, theme }) => (active ? $themeColor : theme.borderColor)};
  a {
    padding: 4px 0 4px 14px;
    font-size: 14px;
    color: ${({ active, $themeColor, theme }) => (active ? $themeColor : theme.textGreyTintedLighterInverse)};
    text-decoration: none !important;
    border-left: 1px solid;
    border-left-color: ${({ active, $themeColor }) => (active ? $themeColor : 'transparent')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &:hover {
      color: ${({ $themeColor }) => $themeColor};
    }
  }
`
const TocH1 = styled(Link)`
  display: block;
`

const TocH2 = styled(Link)`
  display: block;
  padding-left: 30px !important;
`

const PaginationWrapper = styled.div`
  display: flex;
  padding: ${props => (props.$lastModified ? '30px 0 8px 0' : '30px 0')};
  justify-content: space-between;
`

const LastModifiedWrapper = styled.div`
  padding: 0 0 30px 0;
  color: ${props => props.theme.timeColor};
  font-size: 15px;
  a {
    color: ${props => props.theme.timeColor};
    &:hover {
      color: ${({ $themeColor }) => $themeColor};
    }
  }
`

const PaginationLink = styled(Link)`
  display: flex;
  border-radius: 3px;
  border: 1px solid ${props => props.theme.borderColor};
  width: ${props => (props.$hasbothpaginators ? '50%' : '100%')};
  background-color: ${props => props.theme.bgSecondLightest};
  cursor: pointer;
  padding: 5px 15px;
  align-items: center;
  color: ${props => props.theme.textGreyTintedLighterInverse} !important;
  text-decoration: none !important;
  outline: none !important;
  transition: background-color 0.15s ease;

  &:hover,
  &:focus,
  &:focus {
    background-color: ${props => props.theme.hoverBg};
    .sub-head {
      color: ${props => props.theme.textGreyTintedLighterInverse};
    }
    .head,
    .arrow-container {
      color: ${props => props.$themecolor};
    }
  }
`

const PreviousPagination = styled(PaginationLink)`
  border-right: ${props => (props.$hasbothpaginators ? '0 none' : `1px solid ${props.theme.borderColor}`)};
  border-top-right-radius: ${props => (props.$hasbothpaginators ? '0' : '3px')};
  border-bottom-right-radius: ${props => (props.$hasbothpaginators ? '0' : '3px')};
`
const NextPagination = styled(PaginationLink)`
  border-top-left-radius: ${props => (props.$hasbothpaginators ? '0' : '3px')};
  border-bottom-left-radius: ${props => (props.$hasbothpaginators ? '0' : '3px')};
`

const PaginationTitleContainer = styled.div`
  flex: 1 1 auto;
  min-width: 1px;
  text-align: ${props => (props.$alignRight ? 'right' : 'left')};
  padding-left: ${props => (props.$alignRight ? '10px' : '0')};
  padding-right: ${props => (props.$alignRight ? '0' : '10px')};
`
const PaginationSubhead = styled.div`
  font-size: 13px;
  margin-bottom: 5px;
`
const PaginationHead = styled.div`
  color: ${props => props.theme.textGreyTinted} !important;
  font-size: 16px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`
const PaginationArrowContainer = styled.div`
  font-size: 15px;
`
